import { keepPreviousData, queryOptions } from '@tanstack/react-query'

import { apiClient } from './client'
import type {
  AgeingRun,
  ApplyForRoundRequest,
  ApproveRoundRequest,
  CancelRoundRequest,
  ClassificationOverrideRequest,
  ClassificationRun,
  ClassificationScheme,
  ClassificationSchemeRequest,
  DisburseRoundRequest,
  DisclosureConfig,
  DisclosureConfigRequest,
  GuaranteeOffsetRequest,
  LoanAccountDetail,
  LoanAccountPage,
  LoanAccountStatus,
  LoanCycleDetail,
  LoanPrerequisites,
  LoanProductDetail,
  LoanProductPage,
  LoanProductRequest,
  LoanProductStatus,
  LoanSchedule,
  OpenLoanAccountRequest,
  PortfolioRunRequest,
  PostRepaymentRequest,
  RecordAppraisalRequest,
  RecordRecoveryRequest,
  RejectRoundRequest,
  RepaymentPosted,
  RestructureRoundRequest,
  SettleRoundRequest,
  SettlementQuote,
  WaivePenaltyRequest,
  WriteOffRoundRequest,
} from './loans-types'
import type { DualControlResult } from './types'

/**
 * The loan endpoints.
 *
 * Rounds are addressed directly — `/loan-cycles/{id}/…`, never `/loan-accounts/{id}/cycles/{id}/…`
 * — because every financial operation belongs to a round and the account does not know what is
 * owed. Only the round does.
 *
 * Five of these answer a {@link DualControlResult} instead of the thing that was asked for:
 * approval, disbursement, product create, product amend, and putting a classification scheme in
 * force. `awaitingApproval: true` means the command is stored, nothing has changed, and the screen
 * says "sent for approval" rather than "done".
 *
 * Five more are countersigned but answer with the round itself — penalty waiver, restructure,
 * write-off, guarantee offset and classification override. When those are queued the server
 * returns the round *unchanged*, so the response cannot be read as proof the action took effect.
 * `describeCountersignedOutcome` in `features/loans/dual-control.tsx` is how the screens say that
 * honestly; nothing here pretends the distinction is not there.
 */

/** The spec publishes some identifiers as `["integer","string"]`; this client holds strings. */
const toId = (value: string | number): string => String(value)

/**
 * Query keys for the loans module, declared beside the queries that use them.
 *
 * A round has keys of its own rather than living under its account. It is the round that every
 * command acts on, and after a repayment the round, its schedule and any settlement quote are all
 * stale while the *account* is only stale in the one figure the list shows.
 */
export const loanKeys = {
  all: ['loans'] as const,
  accounts: () => [...loanKeys.all, 'accounts'] as const,
  accountLists: () => [...loanKeys.accounts(), 'list'] as const,
  accountList: (query: LoanAccountListQuery) => [...loanKeys.accountLists(), query] as const,
  account: (accountId: string) => [...loanKeys.accounts(), 'detail', accountId] as const,
  cycles: () => [...loanKeys.all, 'cycles'] as const,
  cycle: (cycleId: string) => [...loanKeys.cycles(), 'detail', cycleId] as const,
  schedule: (cycleId: string) => [...loanKeys.cycles(), 'schedule', cycleId] as const,
  settlementQuote: (cycleId: string, asOf: string | null) =>
    [...loanKeys.cycles(), 'settlement-quote', cycleId, asOf] as const,
  prerequisites: (customerId: string, productId: string, principalMinor: number | null) =>
    [...loanKeys.all, 'prerequisites', customerId, productId, principalMinor] as const,
  products: () => [...loanKeys.all, 'products'] as const,
  productLists: () => [...loanKeys.products(), 'list'] as const,
  productList: (query: LoanProductListQuery) => [...loanKeys.productLists(), query] as const,
  product: (productId: string) => [...loanKeys.products(), 'detail', productId] as const,
  classificationSchemes: () => [...loanKeys.all, 'classification-schemes'] as const,
  disclosureConfig: () => [...loanKeys.all, 'disclosure-config'] as const,
}

export interface LoanAccountListQuery {
  customerId?: string
  groupId?: string
  branchId?: string
  status?: LoanAccountStatus
  /** Free text over the account number and the customer's name. */
  search?: string
  page?: number
  pageSize?: number
}

export const loanAccountsQueryOptions = (query: LoanAccountListQuery) =>
  queryOptions({
    queryKey: loanKeys.accountList(query),
    queryFn: async () => {
      const response = await apiClient.get<LoanAccountPage>('/loan-accounts', { params: query })
      return response.data
    },
    // The page on screen stays readable while the next one is fetched, rather than blanking on
    // every keystroke in the search box.
    placeholderData: keepPreviousData,
  })

export const loanAccountQueryOptions = (accountId: string) =>
  queryOptions({
    queryKey: loanKeys.account(accountId),
    queryFn: async () => {
      const response = await apiClient.get<LoanAccountDetail>(
        `/loan-accounts/${encodeURIComponent(accountId)}`,
      )
      return response.data
    },
    enabled: accountId !== '',
  })

/**
 * The savings, deposit and passbook gates for one customer against one product.
 *
 * Gated on `loans.account.open` — not on `loans.view`. A loan officer holding `loans.view` and
 * `loans.apply` but not `loans.account.open` gets a 403 here, which is a permission notice on the
 * panel and not a broken screen. The screens check for that before they render the panel.
 *
 * `principalMinor` fixes the deposit the gate is measured against. Omitted, the server measures
 * against the product's smallest loan, which is what account opening asks about.
 */
export const loanPrerequisitesQueryOptions = (
  customerId: string,
  productId: string,
  principalMinor?: number,
) =>
  queryOptions({
    queryKey: loanKeys.prerequisites(customerId, productId, principalMinor ?? null),
    queryFn: async () => {
      // The gates are the Savings module's to answer, at its own address. The deposit figure is an
      // ARGUMENT here, never computed by the server from a product -- passing none asks only about
      // existence (account, deposit account, passbook), which is what account opening needs.
      const response = await apiClient.get<LoanPrerequisites>(
        `/savings/customers/${encodeURIComponent(customerId)}/loan-prerequisites`,
        { params: { requiredSecurityDepositMinor: principalMinor ?? undefined } },
      )
      return response.data
    },
    enabled: customerId !== '' && productId !== '',
    // A deposit paid in at the counter two minutes ago is exactly the case this answers, so the
    // answer is never served from a cache filled before it.
    staleTime: 0,
  })

export async function openLoanAccount(request: OpenLoanAccountRequest): Promise<LoanAccountDetail> {
  const response = await apiClient.post<LoanAccountDetail>('/loan-accounts', request)
  return response.data
}

export const loanCycleQueryOptions = (cycleId: string) =>
  queryOptions({
    queryKey: loanKeys.cycle(cycleId),
    queryFn: async () => {
      const response = await apiClient.get<LoanCycleDetail>(
        `/loan-cycles/${encodeURIComponent(cycleId)}`,
      )
      return response.data
    },
    enabled: cycleId !== '',
  })

/**
 * The schedule as it was generated at disbursement.
 *
 * Gated on `loans.schedule.view` separately from `loans.view`, so a reader who can see the round
 * may still not see its instalments. The tab says that rather than showing an empty table.
 */
export const loanScheduleQueryOptions = (cycleId: string, enabled = true) =>
  queryOptions({
    queryKey: loanKeys.schedule(cycleId),
    queryFn: async () => {
      const response = await apiClient.get<LoanSchedule>(
        `/loan-cycles/${encodeURIComponent(cycleId)}/schedule`,
      )
      return response.data
    },
    enabled: enabled && cycleId !== '',
  })

/**
 * What clearing the round costs, under the product's rebate rule.
 *
 * Never cached: the quote is the figure the member is told and the figure the settlement is
 * checked against, and one computed before today's penalty accrual would be refused as changed.
 */
export const settlementQuoteQueryOptions = (cycleId: string, asOf: string | null, enabled = true) =>
  queryOptions({
    queryKey: loanKeys.settlementQuote(cycleId, asOf),
    queryFn: async () => {
      const response = await apiClient.get<SettlementQuote>(
        `/loan-cycles/${encodeURIComponent(cycleId)}/settlement-quote`,
        { params: asOf ? { asOf } : undefined },
      )
      return response.data
    },
    enabled: enabled && cycleId !== '',
    staleTime: 0,
    gcTime: 0,
  })

export async function applyForRound(
  accountId: string,
  request: ApplyForRoundRequest,
): Promise<LoanCycleDetail> {
  const response = await apiClient.post<LoanCycleDetail>(
    `/loan-accounts/${encodeURIComponent(accountId)}/cycles`,
    request,
  )
  return response.data
}

export async function recordAppraisal(
  cycleId: string,
  request: RecordAppraisalRequest,
): Promise<LoanCycleDetail> {
  const response = await apiClient.post<LoanCycleDetail>(
    `/loan-cycles/${encodeURIComponent(cycleId)}/appraisal`,
    request,
  )
  return response.data
}

/** Dual control. The approver is never the applicant, whatever the branch's policy says. */
export async function approveRound(
  cycleId: string,
  request: ApproveRoundRequest,
): Promise<DualControlResult> {
  const response = await apiClient.post<DualControlResult>(
    `/loan-cycles/${encodeURIComponent(cycleId)}/approval`,
    request,
  )
  return response.data
}

export async function rejectRound(
  cycleId: string,
  request: RejectRoundRequest,
): Promise<LoanCycleDetail> {
  const response = await apiClient.post<LoanCycleDetail>(
    `/loan-cycles/${encodeURIComponent(cycleId)}/rejection`,
    request,
  )
  return response.data
}

export async function cancelRound(
  cycleId: string,
  request: CancelRoundRequest,
): Promise<LoanCycleDetail> {
  const response = await apiClient.post<LoanCycleDetail>(
    `/loan-cycles/${encodeURIComponent(cycleId)}/cancellation`,
    request,
  )
  return response.data
}

/**
 * Dual control, and refused to whoever approved the round — which is what makes three people the
 * minimum before cash leaves the institution.
 */
export async function disburseRound(
  cycleId: string,
  request: DisburseRoundRequest,
): Promise<DualControlResult> {
  const response = await apiClient.post<DualControlResult>(
    `/loan-cycles/${encodeURIComponent(cycleId)}/disbursement`,
    request,
  )
  return response.data
}

export async function postRepayment(
  cycleId: string,
  request: PostRepaymentRequest,
): Promise<RepaymentPosted> {
  const response = await apiClient.post<RepaymentPosted>(
    `/loan-cycles/${encodeURIComponent(cycleId)}/repayments`,
    request,
  )
  return response.data
}

export async function settleRound(
  cycleId: string,
  request: SettleRoundRequest,
): Promise<RepaymentPosted> {
  const response = await apiClient.post<RepaymentPosted>(
    `/loan-cycles/${encodeURIComponent(cycleId)}/settlement`,
    request,
  )
  return response.data
}

/**
 * Countersigned, and it answers with the round rather than a {@link DualControlResult}. When the
 * branch's policy defers it the round comes back exactly as it was — see the note at the top.
 */
export async function waivePenalty(
  cycleId: string,
  request: WaivePenaltyRequest,
): Promise<LoanCycleDetail> {
  const response = await apiClient.post<LoanCycleDetail>(
    `/loan-cycles/${encodeURIComponent(cycleId)}/penalty-waiver`,
    request,
  )
  return response.data
}

/** Countersigned, answers with the round, and posts nothing: rescheduling moves no money. */
export async function restructureRound(
  cycleId: string,
  request: RestructureRoundRequest,
): Promise<LoanCycleDetail> {
  const response = await apiClient.post<LoanCycleDetail>(
    `/loan-cycles/${encodeURIComponent(cycleId)}/restructure`,
    request,
  )
  return response.data
}

/** Countersigned, answers with the round. */
export async function writeOffRound(
  cycleId: string,
  request: WriteOffRoundRequest,
): Promise<LoanCycleDetail> {
  const response = await apiClient.post<LoanCycleDetail>(
    `/loan-cycles/${encodeURIComponent(cycleId)}/write-off`,
    request,
  )
  return response.data
}

export async function recordRecovery(
  cycleId: string,
  request: RecordRecoveryRequest,
): Promise<LoanCycleDetail> {
  const response = await apiClient.post<LoanCycleDetail>(
    `/loan-cycles/${encodeURIComponent(cycleId)}/recovery`,
    request,
  )
  return response.data
}

/** Countersigned, answers with the round. Takes one member's collateral for another's debt. */
export async function executeGuaranteeOffset(
  cycleId: string,
  request: GuaranteeOffsetRequest,
): Promise<LoanCycleDetail> {
  const response = await apiClient.post<LoanCycleDetail>(
    `/loan-cycles/${encodeURIComponent(cycleId)}/guarantee-offset`,
    request,
  )
  return response.data
}

/** Countersigned, answers with the round. Cleared automatically if the facility worsens. */
export async function overrideClassification(
  cycleId: string,
  request: ClassificationOverrideRequest,
): Promise<LoanCycleDetail> {
  const response = await apiClient.post<LoanCycleDetail>(
    `/loan-cycles/${encodeURIComponent(cycleId)}/classification-override`,
    request,
  )
  return response.data
}

/* ─── Products ───────────────────────────────────────────────────────────────────────────────── */

export interface LoanProductListQuery {
  status?: LoanProductStatus
  /** Free text over the product code and name. */
  search?: string
  page?: number
  pageSize?: number
}

export const loanProductsQueryOptions = (query: LoanProductListQuery) =>
  queryOptions({
    queryKey: loanKeys.productList(query),
    queryFn: async () => {
      const response = await apiClient.get<LoanProductPage>('/loan-products', { params: query })
      return response.data
    },
    placeholderData: keepPreviousData,
    // Products change when somebody countersigns a change to one, which is rare.
    staleTime: 5 * 60 * 1000,
  })

export const loanProductQueryOptions = (productId: string, enabled = true) =>
  queryOptions({
    queryKey: loanKeys.product(productId),
    queryFn: async () => {
      const response = await apiClient.get<LoanProductDetail>(
        `/loan-products/${encodeURIComponent(productId)}`,
      )
      return response.data
    },
    enabled: enabled && productId !== '',
  })

/** Dual control. Sets the rate, the fees and the penalty rule every loan on it will carry. */
export async function createLoanProduct(request: LoanProductRequest): Promise<DualControlResult> {
  const response = await apiClient.post<DualControlResult>('/loan-products', request)
  return response.data
}

/**
 * Dual control. Never reaches a round that is already approved: terms are frozen onto the round
 * at approval, so an amendment prices the next loan and not the one somebody has signed for.
 */
export async function updateLoanProduct(
  productId: string,
  request: LoanProductRequest,
): Promise<DualControlResult> {
  const response = await apiClient.put<DualControlResult>(
    `/loan-products/${encodeURIComponent(productId)}`,
    request,
  )
  return response.data
}

/* ─── Portfolio ──────────────────────────────────────────────────────────────────────────────── */

export const classificationSchemesQueryOptions = () =>
  queryOptions({
    queryKey: loanKeys.classificationSchemes(),
    queryFn: async () => {
      const response = await apiClient.get<ClassificationScheme[]>('/loan-classification-schemes')
      // A bare array, not a page. Narrowed rather than trusted: a shape this client does not
      // recognise must degrade to an empty list, not to a blank screen behind the panel.
      return Array.isArray(response.data) ? response.data : []
    },
    staleTime: 5 * 60 * 1000,
  })

/**
 * Which rate headlines the pre-agreement disclosure (questionnaire item 9). Always answers: with
 * no row recorded the server returns its default with `isConfigured: false`.
 */
export const disclosureConfigQueryOptions = () =>
  queryOptions({
    queryKey: loanKeys.disclosureConfig(),
    queryFn: async () => {
      const response = await apiClient.get<DisclosureConfig>('/loans/disclosure-config')
      return response.data
    },
    staleTime: 5 * 60 * 1000,
  })

/**
 * Dual control. Chooses the disclosure headline — a current-value row, overwritten in place. All
 * three rates stay on the document whichever one this promotes.
 */
export async function setDisclosureConfig(
  request: DisclosureConfigRequest,
): Promise<DualControlResult> {
  const response = await apiClient.put<DualControlResult>('/loans/disclosure-config', request)
  return response.data
}

/** Dual control. Puts buckets and provisioning rates into force from a date. */
export async function createClassificationScheme(
  request: ClassificationSchemeRequest,
): Promise<DualControlResult> {
  const response = await apiClient.post<DualControlResult>(
    '/loan-classification-schemes',
    request,
  )
  return response.data
}

/**
 * Ages a branch's live rounds and accrues penalty. Safe to re-run: the same day twice charges
 * once. Gated on `ops.jobs.trigger` — this is the nightly job, run by hand.
 */
export async function runAgeing(request: PortfolioRunRequest): Promise<AgeingRun> {
  const response = await apiClient.post<AgeingRun>('/loans/ageing-runs', request)
  // The branch identifier arrives as a number on this response and is a string everywhere this
  // application uses one, exactly as `reference-api.ts` normalises it for branches.
  return { ...response.data, branchId: toId(response.data.branchId) }
}

/** Classifies a branch's rounds and posts the provision movement. Gated on `ledger.provision.run`. */
export async function runClassification(request: PortfolioRunRequest): Promise<ClassificationRun> {
  const response = await apiClient.post<ClassificationRun>('/loans/classification-runs', request)
  return { ...response.data, branchId: toId(response.data.branchId) }
}
